import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, Folder, FileText, ArrowLeft, FolderPlus, Trash2, Pencil, Clock, Lock, Crown, BookOpen, Sparkles, LayoutDashboard, Users, Check, Database, PenTool, BarChart, Headset, X, AlertTriangle, ChevronRight, Plus, Search, Filter } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';
import TeamGate from '../components/TeamGate';
import api, { BACKEND_URL } from '../services/api';
import { useTeam } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';
import PlayDesigner from '../components/PlayDesigner';
import PlayThumbnail from '../components/PlayThumbnail';
import { cn } from '../lib/utils';

const Playbook = () => {
  const { t } = useTranslation();
  const [playbooks, setPlaybooks] = useState([]);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { hasPermission, plan, team } = useTeam();
  const { user } = useAuth();
  const canManage = hasPermission('manage_playbook');

  // Create Playbook State
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newPlaybookName, setNewPlaybookName] = useState('');
  const [newPlaybookCategory, setNewPlaybookCategory] = useState('offense');
  const [newPlaybookDesc, setNewPlaybookDesc] = useState('');

  // Upload File State
  const [isFileUploadOpen, setIsFileUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDesc, setUploadDesc] = useState('');

  // File Viewer State
  const [viewFile, setViewFile] = useState(null);

  // Delete File State
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Edit File State
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [fileToEdit, setFileToEdit] = useState(null);
  const [editDesc, setEditDesc] = useState('');

  // Play Designer State
  const [isDesignerOpen, setIsDesignerOpen] = useState(false);
  const [activePlay, setActivePlay] = useState(null);
  const [designedPlays, setDesignedPlays] = useState([]);

  // Play Limit State (for free teams)
  const [playLimitInfo, setPlayLimitInfo] = useState({ count: 0, limit: null, plan: 'free' });

  const confirmDeleteFile = (file, e) => {
    e.stopPropagation(); // Prevent opening viewer
    setFileToDelete(file);
    setIsDeleteOpen(true);
  };

  const openEditFile = (file, e) => {
    e.stopPropagation(); // Prevent opening viewer
    setFileToEdit(file);
    setEditDesc(file.description || '');
    setIsEditOpen(true);
  };

  const handleEditFile = async () => {
    if (!fileToEdit || !selectedPlaybook) return;

    try {
      await api.put(`/playbooks/${selectedPlaybook.id}/files/${fileToEdit.id}`, {
        description: editDesc
      });

      toast({
        title: t('common.success'),
        description: t('playbook.successUpdate'),
      });

      setIsEditOpen(false);
      setFileToEdit(null);
      setEditDesc('');
      fetchPlaybookDetails(selectedPlaybook.id);
    } catch (error) {
      console.error('Error updating file:', error);
      toast({
        title: t('common.error'),
        description: t('playbook.errorUpdate'),
        variant: 'destructive',
      });
    }
  };

  const handleDeleteFile = async () => {
    if (!fileToDelete || !selectedPlaybook) return;

    try {
      await api.delete(`/playbooks/${selectedPlaybook.id}/files/${fileToDelete.id}`);

      toast({
        title: t('common.success'),
        description: t('playbook.successDelete'),
      });

      setIsDeleteOpen(false);
      setFileToDelete(null);
      if (viewFile && viewFile.id === fileToDelete.id) {
        setViewFile(null);
      }
      fetchPlaybookDetails(selectedPlaybook.id);
      fetchPlayLimitInfo();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast({
        title: t('common.error'),
        description: t('playbook.errorDelete'),
        variant: 'destructive',
      });
    }
  };

  const fileInputRef = useRef(null);
  const { toast } = useToast();

  // Fetch play limit info
  const fetchPlayLimitInfo = async () => {
    try {
      const response = await api.get('/plays/count', {
        params: { team_id: user?.team_id }
      });
      setPlayLimitInfo(response.data);
    } catch (error) {
      console.error('Error fetching play limit info:', error);
    }
  };

  useEffect(() => {
    fetchPlaybooks();
    if (user?.team_id) {
      fetchPlayLimitInfo();
    }
  }, [user?.team_id]);

  const fetchPlaybooks = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/playbooks/');
      setPlaybooks(response.data);
    } catch (error) {
      console.error('Error fetching playbooks:', error);
      toast({
        title: t('common.error'),
        description: t('playbook.errorLoad'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPlaybookDetails = async (id) => {
    try {
      const response = await api.get(`/playbooks/${id}`);
      setSelectedPlaybook(response.data);
      // Also fetch designed plays for this playbook
      fetchDesignedPlays(id);
    } catch (error) {
      console.error('Error fetching playbook details:', error);
      toast({
        title: t('common.error'),
        description: t('playbook.errorDetails'),
        variant: 'destructive',
      });
    }
  };

  const fetchDesignedPlays = async (playbookId) => {
    try {
      const response = await api.get('/plays', {
        params: { team_id: team?.id, playbook_id: playbookId }
      });
      setDesignedPlays(response.data);
    } catch (error) {
      console.error('Error fetching designed plays:', error);
    }
  };

  const handleCreatePlaybook = async () => {
    if (!newPlaybookName) return;

    try {
      await api.post('/playbooks/', {
        name: newPlaybookName,
        category: newPlaybookCategory,
        description: newPlaybookDesc
      });

      toast({
        title: t('common.success'),
        description: t('playbook.successFolder'),
      });

      setIsCreateOpen(false);
      setNewPlaybookName('');
      setNewPlaybookDesc('');
      fetchPlaybooks();
    } catch (error) {
      console.error('Error creating playbook:', error);
      toast({
        title: t('common.error'),
        description: t('playbook.errorFolder'),
        variant: 'destructive',
      });
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile || !selectedPlaybook) return;

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('description', uploadDesc);

      await api.post(`/playbooks/${selectedPlaybook.id}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast({
        title: t('common.success'),
        description: t('playbook.successUpload'),
      });

      setIsFileUploadOpen(false);
      setUploadFile(null);
      setUploadDesc('');
      fetchPlaybookDetails(selectedPlaybook.id);
      fetchPlayLimitInfo();
    } catch (error) {
      console.error('Error uploading file:', error);

      const errorData = error.response?.data?.detail;
      if (errorData?.code === 'PLAY_LIMIT_REACHED') {
        toast({
          title: t('analytics.premiumPromotion.title') || 'Premium Gerekli',
          description: errorData.message || t('playbook.limitReached'),
          variant: 'destructive',
          action: (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPremiumModalOpen(true)}
              className="bg-white text-black hover:bg-gray-100"
            >
              {t('common.upgrade') || 'Upgrade'}
            </Button>
          ),
        });
      } else {
        toast({
          title: t('common.error'),
          description: t('playbook.errorUpload'),
          variant: 'destructive',
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveDesignedPlay = async (canvasData) => {
    try {
      if (activePlay) {
        // Update existing
        await api.put(`/plays/${activePlay.id}`, {
          canvas_data: canvasData
        });
        toast({ title: t('common.success'), description: t('playbook.playUpdated') });
      } else {
        // Create new
        const name = prompt(t('playbook.enterPlayName'), t('playbook.newPlay'));
        if (!name) return;

        await api.post('/plays', {
          name,
          category: selectedPlaybook.category,
          playbook_id: selectedPlaybook.id,
          canvas_data: canvasData
        }, {
          params: { team_id: team?.id }
        });
        toast({ title: t('common.success'), description: t('playbook.playSaved') });
      }
      setIsDesignerOpen(false);
      setActivePlay(null);
      fetchDesignedPlays(selectedPlaybook.id);
      fetchPlayLimitInfo(); // Refresh play limit after save
    } catch (error) {
      console.error('Error saving play:', error);
      // Check for limit reached error
      const errorDetail = error.response?.data?.detail;
      if (errorDetail?.code === 'PLAY_LIMIT_REACHED') {
        toast({
          title: t('playbook.playLimitReached'),
          description: t('playbook.playLimitReachedDesc', { limit: errorDetail.limit }),
          variant: 'destructive'
        });
      } else {
        toast({ title: t('common.error'), description: t('playbook.playSaveError'), variant: 'destructive' });
      }
    }
  };

  const openDesignerForEdit = (play, e) => {
    e.stopPropagation();
    if (plan !== 'premium' && user?.role !== 'admin') {
      setIsDesignerOpen(true); // Still open the "Premium required" dialog
      return;
    }
    setActivePlay(play);
    setIsDesignerOpen(true);
  };

  const handleDeletePlay = async (playId, e) => {
    e.stopPropagation();
    if (!window.confirm(t('playbook.deletePlayConfirm'))) return;
    try {
      await api.delete(`/plays/${playId}`);
      toast({ title: t('common.success'), description: t('playbook.playDeleted') });
      fetchDesignedPlays(selectedPlaybook.id);
      fetchPlayLimitInfo(); // Refresh play limit after delete
    } catch (error) {
      console.error('Error deleting play:', error);
      toast({ title: t('common.error'), description: t('playbook.playDeleteError'), variant: 'destructive' });
    }
  };

  // Helper to determine if file is an image
  const isImage = (file) => {
    if (!file?.file_url) return false;
    const isImgType = file.file_type?.startsWith('image/');
    const isImgExt = file.file_url.match(/\.(jpeg|jpg|gif|png|webp|svg)$/i);
    return !!(isImgType || isImgExt);
  };

  const isPdf = (file) => {
    if (!file?.file_url) return false;
    return file.file_type === 'application/pdf' || file.file_url.toLowerCase().endsWith('.pdf');
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  return (
    <TeamGate title={t('playbook.title')}>
      <div className="min-h-screen bg-[#0a0a0b] text-white pb-24 animate-in fade-in duration-500">
        {/* Mobile Sticky Header */}
        <div className="lg:hidden p-4 bg-[#0a0a0b]/80 backdrop-blur-xl sticky top-0 z-[100] border-b border-white/5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              {selectedPlaybook && (
                <button
                  onClick={() => setSelectedPlaybook(null)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-90"
                >
                  <ArrowLeft className="w-5 h-5 text-snapp-blue" />
                </button>
              )}
              <h2 className="text-xl font-black text-white tracking-tight uppercase">
                {selectedPlaybook ? selectedPlaybook.name : t('playbook.title')}
              </h2>
            </div>
            {canManage && !selectedPlaybook && (
              <button
                onClick={() => setIsCreateOpen(true)}
                className="p-2 bg-snapp-blue text-white rounded-xl active:scale-90 shadow-lg shadow-blue-500/20"
              >
                <FolderPlus className="w-5 h-5" />
              </button>
            )}
          </div>
          {/* Breadcrumbs/Meta */}
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
            <div className="px-3 py-1 bg-white/5 rounded-full whitespace-nowrap">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                {selectedPlaybook ? selectedPlaybook.category.replace('_', ' ') : `${playbooks.length} Folders`}
              </span>
            </div>
            {selectedPlaybook && (
              <div className="px-3 py-1 bg-snapp-purple/10 border border-snapp-purple/20 rounded-full whitespace-nowrap">
                <span className="text-[10px] font-black text-snapp-purple uppercase tracking-widest">
                  {(selectedPlaybook.files?.length || 0) + designedPlays.length} Items
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 lg:p-8 space-y-8">
          {/* Desktop Header */}
          <div className="hidden lg:flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-4">
                {selectedPlaybook && (
                  <button
                    onClick={() => setSelectedPlaybook(null)}
                    className="p-3 bg-[#1a1a1b] border border-white/5 rounded-2xl hover:bg-white/5 transition-all active:scale-95 shadow-xl"
                  >
                    <ArrowLeft className="w-6 h-6 text-snapp-blue" />
                  </button>
                )}
                <div>
                  <h1 className="text-3xl font-black text-white uppercase tracking-tight">
                    {selectedPlaybook ? selectedPlaybook.name : t('playbook.title')}
                  </h1>
                  <p className="text-gray-400 font-medium mt-1">
                    {selectedPlaybook ? selectedPlaybook.description || t('playbook.viewingFolder') : t('playbook.subtitle')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              {!selectedPlaybook ? (
                canManage && (
                  <button
                    onClick={() => setIsCreateOpen(true)}
                    className="bg-snapp-blue hover:bg-snapp-blue/90 text-white px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-95 shadow-lg shadow-blue-500/20 flex items-center gap-2"
                  >
                    <FolderPlus className="w-4 h-4" /> {t('playbook.addFolder')}
                  </button>
                )
              ) : (
                canManage && (
                  <>
                    {playLimitInfo.limit !== null && (
                      <div className={cn(
                        "flex items-center gap-2 px-4 py-2.5 rounded-2xl border transition-all",
                        playLimitInfo.count >= playLimitInfo.limit ? "bg-red-500/10 text-red-400 border-red-500/20 shadow-lg shadow-red-500/10" : "bg-amber-500/10 text-amber-400 border-amber-500/20 shadow-lg shadow-amber-500/10"
                      )}>
                        {playLimitInfo.count >= playLimitInfo.limit ? <AlertTriangle className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                        <span className="text-[10px] font-black uppercase tracking-widest">{playLimitInfo.count}/{playLimitInfo.limit} Plays Used</span>
                      </div>
                    )}
                    <button
                      onClick={() => { setActivePlay(null); setIsDesignerOpen(true); }}
                      className="bg-snapp-purple hover:bg-snapp-purple/90 text-white px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-95 shadow-lg shadow-purple-500/20 flex items-center gap-2"
                    >
                      <Pencil className="w-4 h-4" /> {t('playbook.designer')}
                    </button>
                    <button
                      onClick={() => setIsFileUploadOpen(true)}
                      className="bg-snapp-blue hover:bg-snapp-blue/90 text-white px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-95 shadow-lg shadow-blue-500/20 flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" /> {t('playbook.uploadFile')}
                    </button>
                  </>
                )
              )}
            </div>
          </div>

          {!selectedPlaybook ? (
            /* Folder List View */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {playbooks.map((playbook) => (
                <div
                  key={playbook.id}
                  onClick={() => fetchPlaybookDetails(playbook.id)}
                  className="bg-[#1a1a1b]/60 backdrop-blur-xl border border-white/5 rounded-[2rem] p-6 shadow-xl relative overflow-hidden group transition-all duration-300 active:scale-[0.98] cursor-pointer"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="p-4 bg-snapp-blue/10 rounded-2xl group-hover:scale-110 transition-transform duration-500">
                        <Folder className="w-8 h-8 text-snapp-blue" />
                      </div>
                      <div className="px-3 py-1 bg-white/5 rounded-full">
                        <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{playbook.category.replace('_', ' ')}</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-white tracking-tight leading-tight group-hover:text-snapp-blue transition-colors">{playbook.name}</h3>
                      <p className="text-[10px] font-black text-gray-600 uppercase tracking-widest mt-1">Modified {new Date(playbook.created_at).toLocaleDateString()}</p>
                    </div>
                    {playbook.description && (
                      <p className="text-xs text-gray-500 line-clamp-2 font-medium">{playbook.description}</p>
                    )}
                  </div>
                </div>
              ))}

              {playbooks.length === 0 && !isLoading && (
                <div className="col-span-full text-center py-20 bg-[#1a1a1b]/30 rounded-[2.5rem] border border-white/5 border-dashed">
                  <Folder className="w-16 h-16 text-white/5 mx-auto mb-4" />
                  <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">{t('playbook.noPlaybooks')}</p>
                </div>
              )}
            </div>
          ) : (
            /* File List View Enhanced with Thumbnails */
            <div className="space-y-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {/* Designed Plays First */}
                {designedPlays.map((play) => (
                  <div
                    key={play.id}
                    className="bg-[#1a1a1b]/60 backdrop-blur-xl border border-white/5 rounded-[2rem] overflow-hidden group relative transition-all duration-300 shadow-xl active:scale-[0.98] cursor-pointer aspect-[3/4]"
                    onClick={(e) => openDesignerForEdit(play, e)}
                  >
                    <div className="absolute inset-0 bg-[#06140d] flex items-center justify-center overflow-hidden">
                      <PlayThumbnail
                        canvasData={play.canvas_data}
                        width={320}
                        height={360}
                      />
                      {/* Premium Teaser Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20 group-hover:backdrop-blur-none backdrop-blur-[1px] transition-all duration-500">
                        {/* Interactive UI on Hover */}
                        <div className="absolute top-4 right-4 flex gap-2 translate-y-[-10px] opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                          {canManage && (
                            <button
                              onClick={(e) => handleDeletePlay(play.id, e)}
                              className="p-2 bg-red-500/80 hover:bg-red-500 text-white rounded-xl backdrop-blur-md"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="absolute inset-x-0 bottom-0 p-5 bg-black/60 backdrop-blur-md border-t border-white/5">
                      <h4 className="text-white font-black text-sm tracking-tight leading-tight line-clamp-1">{play.name}</h4>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[9px] font-black text-snapp-blue uppercase tracking-widest italic">{t('playbook.designedPlay')}</span>
                        <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-snapp-blue transition-colors" />
                      </div>
                    </div>
                  </div>
                ))}

                {/* Uploaded Files */}
                {selectedPlaybook.files.map((file) => (
                  <div
                    key={file.id}
                    className="bg-[#1a1a1b]/60 backdrop-blur-xl border border-white/5 rounded-[2rem] overflow-hidden group relative transition-all duration-300 shadow-xl active:scale-[0.98] cursor-pointer aspect-[3/4]"
                    onClick={() => setViewFile(file)}
                  >
                    <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0b]">
                      {isImage(file) ? (
                        <img
                          src={getFullUrl(file.file_url)}
                          alt={file.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      ) : isPdf(file) ? (
                        <div className="flex flex-col items-center gap-4">
                          <div className="p-6 bg-red-500/10 rounded-[2rem] group-hover:scale-110 transition-transform duration-500">
                            <FileText className="w-12 h-12 text-red-500" />
                          </div>
                          <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest tracking-[0.2em]">Portable Document</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-4">
                          <div className="p-6 bg-snapp-blue/10 rounded-[2rem] group-hover:scale-110 transition-transform duration-500">
                            <FileText className="w-12 h-12 text-snapp-blue" />
                          </div>
                          <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest tracking-[0.2em]">Universal File</span>
                        </div>
                      )}
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute top-4 right-4 flex gap-2">
                          {canManage && (
                            <>
                              <button
                                onClick={(e) => openEditFile(file, e)}
                                className="p-2 bg-white/10 hover:bg-snapp-blue text-white rounded-xl backdrop-blur-md transition-all active:scale-90"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => confirmDeleteFile(file, e)}
                                className="p-2 bg-white/10 hover:bg-red-500 text-white rounded-xl backdrop-blur-md transition-all active:scale-90"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="absolute inset-x-0 bottom-0 p-5 bg-black/60 backdrop-blur-md border-t border-white/5">
                      <h4 className="text-white font-black text-sm tracking-tight leading-tight line-clamp-1">{file.name}</h4>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest">
                          {new Date(file.created_at).toLocaleDateString()}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-snapp-blue transition-colors" />
                      </div>
                    </div>
                  </div>
                ))}

                {(selectedPlaybook.files.length === 0 && designedPlays.length === 0) && (
                  <div className="col-span-full text-center py-20 bg-[#1a1a1b]/30 rounded-[2.5rem] border border-white/5 border-dashed">
                    <Sparkles className="w-16 h-16 text-white/5 mx-auto mb-4" />
                    <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">{t('playbook.noContent')}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Floating Action Button for Mobile Coaches */}
        {user?.role === 'coach' && (
          <div className="lg:hidden fixed bottom-28 right-6 flex flex-col gap-3 z-[101]">
            {selectedPlaybook && (
              <>
                <button
                  onClick={() => setIsFileUploadOpen(true)}
                  className="w-14 h-14 bg-snapp-blue text-white rounded-2xl shadow-2xl flex items-center justify-center transition-all active:scale-90"
                >
                  <Upload className="w-7 h-7" />
                </button>
                <button
                  onClick={() => { setActivePlay(null); setIsDesignerOpen(true); }}
                  className="w-14 h-14 bg-snapp-purple text-white rounded-2xl shadow-2xl flex items-center justify-center transition-all active:scale-90"
                >
                  <Plus className="w-8 h-8" />
                </button>
              </>
            )}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white">
            <DialogHeader>
              <DialogTitle>{t('playbook.deleteFile')}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-300">
                {t('playbook.deleteConfirmPartial')} <strong>{fileToDelete?.name}</strong>?
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setIsDeleteOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteFile}
                className="bg-red-600 hover:bg-red-700"
              >
                {t('common.delete')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit File Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white">
            <DialogHeader>
              <DialogTitle>{t('playbook.editFile')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('playbook.folderDescription')}</label>
                <Textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  placeholder={t('playbook.descPlaceholder')}
                  className="bg-black/20 border-gray-800 min-h-[100px]"
                />
              </div>
              <Button
                onClick={handleEditFile}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {t('playbook.saveChanges')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* File Viewer Dialog */}
        <Dialog open={!!viewFile} onOpenChange={(open) => !open && setViewFile(null)}>
          <DialogContent className="max-w-6xl h-[90vh] bg-[#0a0a0a] border-gray-800 p-0 flex flex-col md:flex-row overflow-hidden">
            <DialogHeader className="sr-only">
              <DialogTitle>{viewFile?.name || t('playbook.fileViewer')}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 bg-black flex items-center justify-center p-4 relative overflow-auto">
              {viewFile && (
                isImage(viewFile) ? (
                  <img
                    src={getFullUrl(viewFile.file_url)}
                    alt={viewFile.name}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <iframe
                    src={`${getFullUrl(viewFile.file_url)}#toolbar=0&navpanes=0&scrollbar=0`}
                    className="w-full h-full border-none"
                    title={viewFile.name}
                  />
                )
              )}
            </div>
            {viewFile && (
              <div className="w-full md:w-80 border-l border-gray-800 bg-[#1a1a1b] p-6 overflow-y-auto">
                <DialogHeader className="mb-6">
                  <DialogTitle className="text-white text-xl">{viewFile.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">{t('playbook.folderDescription')}</h4>
                    <p className="text-white text-sm">{viewFile.description || t('playbook.noDescription')}</p>
                  </div>
                  <div className="grid grid-cols-1 gap-4 text-xs">
                    <div>
                      <p className="text-gray-500 mb-1">{t('common.created') || 'Oluşturuldu'}</p>
                      <p className="text-gray-300">{new Date(viewFile.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Play Designer Dialog Overlay */}
        <Dialog open={isDesignerOpen} onOpenChange={(open) => {
          if (!open) {
            setIsDesignerOpen(false);
            setActivePlay(null);
          }
        }}>
          <DialogContent className={
            (user?.role === 'admin' || plan === 'premium')
              ? "max-w-[1300px] h-[95vh] bg-[#0a0a0b] border-gray-800 p-0 overflow-hidden hide-close-button"
              : "bg-[#0a0a0b]/95 backdrop-blur-xl border-gray-800 text-white max-w-2xl p-0 overflow-hidden shadow-2xl"
          }>
            <DialogHeader className="sr-only">
              <DialogTitle>{activePlay?.name || "Designing New Play"}</DialogTitle>
            </DialogHeader>
            {(user?.role === 'admin' || plan === 'premium') ? (
              <PlayDesigner
                initialData={activePlay?.canvas_data}
                playName={activePlay?.name || t('playDesigner.newPlay')}
                onSave={handleSaveDesignedPlay}
                onCancel={() => {
                  setIsDesignerOpen(false);
                  setActivePlay(null);
                }}
                playbookType={selectedPlaybook?.category || 'offense'}
              />
            ) : (
              <div className="relative">
                {/* Header Background Decoration */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-transparent -z-10" />



                <div className="p-8 text-center pt-12">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-tr from-amber-400 to-amber-600 flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(251,191,36,0.3)] animate-pulse">
                    <Crown className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold mb-2 tracking-tight">
                    {t('analytics.premiumPromotion.title')}
                  </h2>
                  <p className="text-gray-400 text-lg">
                    {t('analytics.premiumPromotion.subtitle')}
                  </p>
                </div>

                <div className="px-8 pb-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      {
                        icon: Database,
                        title: t('analytics.premiumPromotion.featureStorage'),
                        desc: t('analytics.premiumPromotion.featureStorageDesc'),
                        color: 'text-blue-500',
                        bg: 'bg-blue-500/10'
                      },
                      {
                        icon: PenTool,
                        title: t('analytics.premiumPromotion.featureDesigner'),
                        desc: t('analytics.premiumPromotion.featureDesignerDesc'),
                        color: 'text-purple-500',
                        bg: 'bg-purple-500/10'
                      },
                      {
                        icon: BarChart,
                        title: t('analytics.premiumPromotion.featureStats'),
                        desc: t('analytics.premiumPromotion.featureStatsDesc'),
                        color: 'text-amber-500',
                        bg: 'bg-amber-500/10'
                      },
                      {
                        icon: Headset,
                        title: t('analytics.premiumPromotion.featureSupport'),
                        desc: t('analytics.premiumPromotion.featureSupportDesc'),
                        color: 'text-green-500',
                        bg: 'bg-green-500/10'
                      }
                    ].map((feature, index) => {
                      const Icon = feature.icon;
                      return (
                        <div
                          key={index}
                          className="p-5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors group"
                        >
                          <div className={`p-3 rounded-lg ${feature.bg} ${feature.color} w-fit mb-4 group-hover:scale-110 transition-transform`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <h3 className="font-bold text-white mb-2">{feature.title}</h3>
                          <p className="text-sm text-gray-400 leading-relaxed">
                            {feature.desc}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-blue-500/20 flex flex-col items-center justify-center text-center">
                    <p className="text-white font-medium mb-4">
                      {t('analytics.premiumPromotion.contactForUpgrade')}
                    </p>
                    <div className="flex gap-4 w-full">
                      <Button
                        className="flex-1 bg-white text-black hover:bg-gray-200 font-bold"
                        onClick={() => window.open('mailto:info@snapp.ai')}
                      >
                        info@snapp.ai
                      </Button>
                      <Button
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                        onClick={() => setIsDesignerOpen(false)}
                      >
                        {t('analytics.premiumPromotion.close')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TeamGate>
  );
};

export default Playbook;
